import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { twJoin } from 'tailwind-merge';

interface Props {
    currentYear: Date;
    currentMonth: Date;
    selectedDate: Nullable<Date>;
    minDate?: Date;
    maxDate?: Date;
    onMonthClick: (monthIndex: number, isDisabled: boolean) => void;
    focusedMonthIndex: number;
    onFocusedMonthChange?: (index: number) => void;
}

export function MonthsView({
    currentYear,
    currentMonth,
    selectedDate,
    minDate,
    maxDate,
    onMonthClick,
    focusedMonthIndex,
    onFocusedMonthChange,
}: Props) {
    const t = useTranslations();
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const monthNames = useMemo(() => {
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];
        return months.map(month => t(`general.datePicker.months.${month.toLowerCase()}`, { defaultValue: month }));
    }, [t]);

    const shortenedMonthNames = useMemo(() => {
        const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];
        return months.map(month =>
            t(`general.datePicker.shortenedMonths.${month.toLowerCase()}`, { defaultValue: month }),
        );
    }, [t]);

    const months = useMemo(() => {
        const year = currentYear.getFullYear();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return Array.from({ length: 12 }, (_, i) => {
            const date = new Date(year, i, 1);
            let isDisabled = false;
            if (minDate && new Date(year, i + 1, 0) < minDate) isDisabled = true;
            if (maxDate && date > maxDate) isDisabled = true;

            return {
                month: i,
                isCurrentMonth: i === currentMonth.getMonth() && year === currentYear.getFullYear(),
                isSelected: !!(selectedDate && i === selectedDate.getMonth() && year === selectedDate.getFullYear()),
                isToday: i === today.getMonth() && year === today.getFullYear(),
                isDisabled,
            };
        });
    }, [currentYear, minDate, maxDate, currentMonth, selectedDate]);

    const handleMonthClick = useCallback(
        (monthData: (typeof months)[0], index: number) => {
            onMonthClick(monthData.month, monthData.isDisabled);
            onFocusedMonthChange?.(index);
        },
        [onMonthClick, onFocusedMonthChange],
    );

    const focusMonth = useCallback((index: number) => {
        const button = buttonRefs.current[index];
        if (button && !button.disabled) {
            button.focus();
            return;
        }
        buttonRefs.current.find(button => button != null && !button.disabled)?.focus();
    }, []);

    useEffect(() => {
        if (focusedMonthIndex >= 0 && focusedMonthIndex < buttonRefs.current.length) {
            focusMonth(focusedMonthIndex);
        }
    }, [focusedMonthIndex, focusMonth]);

    useChangeEffect(() => {
        const id = requestAnimationFrame(() => focusMonth(focusedMonthIndex));
        return () => cancelAnimationFrame(id);
    }, focusMonth);

    return (
        <div className="grid h-auto grid-cols-3 gap-2" role="grid">
            {months.map((monthData, index) => (
                <button
                    key={index}
                    ref={el => {
                        buttonRefs.current[index] = el;
                    }}
                    type="button"
                    role="gridcell"
                    tabIndex={index === focusedMonthIndex ? 0 : -1}
                    className={twJoin(
                        'w-full rounded-md py-6 text-md md:py-2 md:text-sm',
                        !monthData.isDisabled && !monthData.isSelected && 'hover:bg-neutral-100',
                        monthData.isToday && !monthData.isSelected && 'border border-neutral-400',
                        monthData.isSelected && 'bg-primary-500 text-white',
                        monthData.isDisabled && 'cursor-not-allowed opacity-50',
                    )}
                    onClick={() => handleMonthClick(monthData, index)}
                    disabled={monthData.isDisabled}
                    aria-label={monthNames[monthData.month]}
                    aria-selected={monthData.isSelected || undefined}>
                    {shortenedMonthNames[monthData.month]}
                </button>
            ))}
        </div>
    );
}
